// src/components/admin/company/ProfileSettings.jsx
import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { companyService } from '../../../services/company';
import { showNotification } from '../../../utils/helpers';
import Button from '../../common/Button';
import Input from '../../common/Input';
import Loading from '../../common/Loading';

const ProfileSettings = () => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ceoData, setCeoData] = useState({
    ceo_name: '',
    ceo_bio: '',
    ceo_avatar: null
  });
  const [previewAvatar, setPreviewAvatar] = useState(null);
  const { showNotification } = useNotification();

  useEffect(() => {
    fetchCeoData();
  }, []);

  const fetchCeoData = async () => {
    try {
      setLoading(true);
      const response = await companyService.getCeoInfo();
      if (response.success) {
        setCeoData({
          ceo_name: response.data.ceo_name || '',
          ceo_bio: response.data.ceo_bio || '',
          ceo_avatar: null
        });
        if (response.data.ceo_avatar) {
          setPreviewAvatar(response.data.ceo_avatar);
        }
      }
    } catch (error) {
      console.error('Error fetching CEO data:', error);
      showNotification('Gagal mengambil data CEO', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCeoData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        showNotification('Format file harus JPG, JPEG, atau PNG', 'error');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showNotification('Ukuran file maksimal 5MB', 'error');
        return;
      }

      setCeoData(prev => ({
        ...prev,
        ceo_avatar: file
      }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewAvatar(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!ceoData.ceo_name.trim()) {
      showNotification('Nama CEO harus diisi', 'error');
      return;
    }

    if (!ceoData.ceo_bio.trim()) {
      showNotification('Bio CEO harus diisi', 'error');
      return;
    }

    try {
      setSubmitting(true);
      
      const formData = new FormData();
      formData.append('ceo_name', ceoData.ceo_name);
      formData.append('ceo_bio', ceoData.ceo_bio);
      
      if (ceoData.ceo_avatar) {
        formData.append('ceo_avatar', ceoData.ceo_avatar);
      }

      const response = await companyService.updateCeoInfo(formData);
      
      if (response.success) {
        showNotification('Profil CEO berhasil diperbarui', 'success');
        fetchCeoData(); // Refresh data
      }
    } catch (error) {
      console.error('Error updating CEO profile:', error);
      showNotification(
        error.response?.data?.message || 'Gagal memperbarui profil CEO',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveAvatar = () => {
    setCeoData(prev => ({
      ...prev,
      ceo_avatar: null
    }));
    setPreviewAvatar(null);
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Pengaturan Profil CEO
        </h2>
        <p className="text-gray-600">
          Kelola informasi profil CEO perusahaan
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Avatar Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Foto CEO
          </label>
          <div className="flex items-center space-x-6">
            <div className="relative">
              {previewAvatar ? (
                <div className="relative">
                  <img
                    src={previewAvatar}
                    alt="CEO Avatar"
                    className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveAvatar}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center border-4 border-white shadow-lg">
                  <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
            <div className="flex-1">
              <input
                type="file"
                id="ceo_avatar"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="ceo_avatar"
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Upload Foto
              </label>
              <p className="text-xs text-gray-500 mt-1">
                JPG, JPEG atau PNG. Maksimal 5MB.
              </p>
            </div>
          </div>
        </div>

        {/* CEO Name */}
        <div>
          <Input
            label="Nama CEO"
            name="ceo_name"
            value={ceoData.ceo_name}
            onChange={handleInputChange}
            placeholder="Masukkan nama lengkap CEO"
            required
          />
        </div>

        {/* CEO Bio */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bio CEO <span className="text-red-500">*</span>
          </label>
          <textarea
            name="ceo_bio"
            value={ceoData.ceo_bio}
            onChange={handleInputChange}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Masukkan bio dan pengalaman CEO..."
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Ceritakan tentang latar belakang, pengalaman, dan visi CEO
          </p>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-6 border-t border-gray-200">
          <Button
            type="submit"
            variant="primary"
            disabled={submitting}
            className="min-w-[120px]"
          >
            {submitting ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Menyimpan...
              </div>
            ) : (
              'Simpan Perubahan'
            )}
          </Button>
        </div>
      </form>

      {/* Info Section */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-blue-800">Tips Profil CEO</h4>
            <ul className="text-sm text-blue-700 mt-1 space-y-1">
              <li>• Gunakan foto professional dengan latar belakang bersih</li>
              <li>• Bio CEO akan ditampilkan di halaman profil perusahaan</li>
              <li>• Sertakan pengalaman dan pencapaian yang relevan</li>
              <li>• Maksimal bio sekitar 500-1000 karakter untuk keterbacaan optimal</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
